import { ColorVariantsItem } from 'components/Pages/ProductDetail/ColorVariants/ColorVariantsItem';
import useEmblaCarousel from 'embla-carousel-react';
import { ArrowIcon } from 'public/svg/IconsSvg';
import { useCallback, useEffect, useState } from 'react';
import { twJoin } from 'tailwind-merge';
import { ColorVariantType } from 'types/product';

const TEST_IDENTIFIER = 'detail-variants-mobile';

type Props = {
    colorVariants: ColorVariantType[];
    currentVariantUuid: string;
};

const productSliderArrowTwClass =
    'absolute top-1/2 z-productSlider -mt-1 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-none bg-black text-white outline-none duration-200 ease-defaultTransition hover:bg-green';

export const ColorVariantsSlider: FC<Props> = ({ currentVariantUuid, colorVariants }) => {
    const [prevButtonEnabled, setPrevButtonEnabled] = useState(false);
    const [nextButtonEnabled, setNextButtonEnabled] = useState(false);

    const [emblaRef, emblaApi] = useEmblaCarousel({
        align: 'start',
        containScroll: 'trimSnaps',
        loop: false,
        skipSnaps: false,
        duration: 20,
        startIndex: 0,
    });

    const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
    const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);

    useEffect(() => {
        if (!emblaApi) {
            return void null;
        }

        const onSelectHandler = () => {
            setPrevButtonEnabled(emblaApi.canScrollPrev());
            setNextButtonEnabled(emblaApi.canScrollNext());
        };
        onSelectHandler();
        emblaApi.on('select', onSelectHandler);

        return () => {
            emblaApi.off('select', onSelectHandler);
        };
    }, [emblaApi]);

    return (
        <div
            data-testid={TEST_IDENTIFIER}
            ref={emblaRef}
            className={twJoin(
                'relative -mx-5 mb-2 min-w-full overflow-hidden px-5 py-1',
                prevButtonEnabled && 'before:block',
                nextButtonEnabled && 'after:block',
                'before:pointer-events-none before:absolute before:bottom-0 before:left-0 before:top-0 before:z-belowOverlay before:hidden before:w-24 before:content-[""] before:[background:linear-gradient(to_right,_#ffffff_0%,_#ffffff00_100%)]',
                'after:pointer-events-none after:absolute after:bottom-0 after:right-0 after:top-0 after:z-belowOverlay after:hidden after:w-24 after:content-[""] after:[background:linear-gradient(to_left,_#ffffff_0%,_#ffffff00_100%)]',
            )}
        >
            <div className="relative flex h-full w-full">
                {colorVariants.map((colorVariant) => (
                    <ColorVariantsItem
                        key={colorVariant.uuid}
                        currentVariantUuid={currentVariantUuid}
                        variant={colorVariant}
                    />
                ))}
            </div>
            {prevButtonEnabled && (
                <button
                    className={twJoin(productSliderArrowTwClass, 'left-5 rotate-90')}
                    tabIndex={0}
                    onClick={scrollPrev}
                >
                    <ArrowIcon className="text-currentColor" />
                </button>
            )}
            {nextButtonEnabled && (
                <button
                    className={twJoin(productSliderArrowTwClass, 'right-5 -rotate-90')}
                    tabIndex={0}
                    onClick={scrollNext}
                >
                    <ArrowIcon className="text-currentColor" />
                </button>
            )}
        </div>
    );
};
